import { useCallback, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { supportedLanguages } from '@/locales';

const EXTERNAL_SCHEME_REGEX = /^[a-z][a-z0-9+.-]*:/i;

function splitPath(path: string) {
  let pathname = path || '/';
  let search = '';
  let hash = '';

  const hashIndex = pathname.indexOf('#');
  if (hashIndex >= 0) {
    hash = pathname.slice(hashIndex);
    pathname = pathname.slice(0, hashIndex);
  }

  const queryIndex = pathname.indexOf('?');
  if (queryIndex >= 0) {
    search = pathname.slice(queryIndex);
    pathname = pathname.slice(0, queryIndex);
  }

  if (!pathname) {
    pathname = '/';
  }

  return { pathname, search, hash };
}

function stripLanguagePrefix(pathname: string) {
  const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const segments = normalizedPath.split('/').filter(Boolean);
  const potentialLang = segments[0];
  const hasLang = potentialLang && supportedLanguages.includes(potentialLang as any);

  if (!hasLang) {
    return {
      lang: 'en',
      pathname: normalizedPath || '/',
      hasLang: false,
    };
  }

  const remainingPath = segments.slice(1).join('/');
  const strippedPath = remainingPath ? `/${remainingPath}` : '/';

  return {
    lang: potentialLang as string,
    pathname: strippedPath,
    hasLang: true,
  };
}

function isExternal(path: string) {
  return EXTERNAL_SCHEME_REGEX.test(path) || path.startsWith('//');
}

/**
 * Hook that handles language-aware routing
 * Extracts language from URL path and updates i18n accordingly
 */
export function useLanguageRouter() {
  const [location, setLocation] = useLocation();
  const { i18n } = useTranslation();

  const { pathname, search, hash } = splitPath(location);
  const { lang: detectedLang, pathname: strippedPathname } = stripLanguagePrefix(pathname);

  const currentLang = detectedLang || 'en';
  const pathWithoutLang = `${strippedPathname}${search}${hash}` || '/';

  const withLanguagePath = useCallback((path: string, lang = currentLang) => {
    if (!path) {
      return lang === 'en' ? '/' : `/${lang}`;
    }

    if (isExternal(path) || path.startsWith('#')) {
      return path;
    }

    const { pathname: rawPathname, search: targetSearch, hash: targetHash } = splitPath(path);
    const { pathname: cleanPathname } = stripLanguagePrefix(rawPathname);

    const normalizedPath = lang === 'en'
      ? cleanPathname
      : cleanPathname === '/'
        ? `/${lang}`
        : `/${lang}${cleanPathname}`;

    return `${normalizedPath}${targetSearch}${targetHash}`;
  }, [currentLang]);

  // Update i18n language when path language changes
  useEffect(() => {
    if (i18n.language !== currentLang) {
      i18n.changeLanguage(currentLang);
    }
  }, [currentLang, i18n]);

  /**
   * Navigate to a path with current language prefix
   */
  const navigate = useCallback((path: string, lang?: string) => {
    const targetLang = lang || currentLang;
    const fullPath = withLanguagePath(path, targetLang);
    setLocation(fullPath);
  }, [currentLang, setLocation, withLanguagePath]);

  /**
   * Change language and navigate to same path in new language
   */
  const changeLanguage = useCallback((newLang: string) => {
    const newPath = withLanguagePath(pathWithoutLang, newLang);
    setLocation(newPath);
  }, [pathWithoutLang, setLocation, withLanguagePath]);

  return {
    currentLang,
    pathWithoutLang,
    location,
    navigate,
    changeLanguage,
    withLanguagePath,
  };
}
